import { ComponentRef, Directive, ElementRef, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { FiltersToggleComponent } from './filters-collapse.component';

interface FilterRegion {
  expanded: boolean;
  toggle: ComponentRef<FiltersToggleComponent>;
  members: Set<HTMLElement>;
}

/** Uma região por elemento pai: todos os blocos de filtro irmãos compartilham o mesmo botão e o mesmo estado. */
const regions = new WeakMap<Element, FilterRegion>();

/**
 * Recolhe os blocos de filtro (`.table-filters`, `.table-filters-row`) no mobile e cria o botão "Filtros" que os expande.
 * No desktop não muda nada: o botão e o recolhimento só existem sob o breakpoint mobile (ver `_filters-collapse.scss`).
 * Nenhum filtro é removido ou alterado; só a visibilidade muda.
 */
@Directive({
  selector: '.table-filters, .table-filters-row',
  standalone: false
})
export class FiltersCollapseDirective implements OnInit, OnDestroy {
  private region?: FilterRegion;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef
  ) { }

  ngOnInit(): void {
    const element = this.elementRef.nativeElement;
    const parent = element.parentElement;
    if (!parent) return;

    let region = regions.get(parent);
    if (!region) {
      const toggle = this.viewContainerRef.createComponent(FiltersToggleComponent);
      const created: FilterRegion = { expanded: false, toggle, members: new Set() };
      toggle.instance.toggled = () => this.toggleRegion(created);
      this.renderer.insertBefore(parent, toggle.location.nativeElement, element);
      regions.set(parent, created);
      region = created;
    }

    this.region = region;
    region.members.add(element);
    this.apply(region);
  }

  ngOnDestroy(): void {
    const region = this.region;
    if (!region) return;

    region.members.delete(this.elementRef.nativeElement);
    if (!region.members.size) {
      const parent = this.elementRef.nativeElement.parentElement;
      if (parent) regions.delete(parent);
      region.toggle.destroy();
    }
  }

  private toggleRegion(region: FilterRegion): void {
    region.expanded = !region.expanded;
    this.apply(region);
  }

  private apply(region: FilterRegion): void {
    region.toggle.instance.expanded = region.expanded;
    region.toggle.changeDetectorRef.detectChanges();
    region.members.forEach(member => member.classList.toggle('ss-filters-collapsed', !region.expanded));
  }
}
